import { Component } from 'react';
import { Modal, OverlayTrigger, Tooltip, Badge, Row, Col } from 'react-bootstrap';
import { inject, observer } from 'mobx-react';
import moment from 'moment/min/moment-with-locales';
import { confirmAlert } from 'react-confirm-alert';

// Components
import applicationRouter from '~/hoc/applicationRouter';
import withLocalization from '../../../hoc/withLocalization';
import DatePicker from '../../../components/DatePicker/DatePicker';
import GenericList from '~/components/GenericList/GenericList';
import { ProjectsWidget } from '../../../components/GenericForm/Widgets/ProjectsWidget';
import { TasksWidget } from '../../../components/GenericForm/Widgets/TasksWidget';
import { AfterBulkCreateFailedResponseModalBody } from './AfterCopyResponse.js';
import UserPromptModal from '../../../components/GenericModal/userPromptModal/index.js';

// Elements
import Button from '~/components/CustomButton/CustomButton.jsx';
import TimePickerInput from '../../../components/TimePicker';
import CheckboxButton from '../../../components/CustomButtonCheckbox/CustomButtonCheckbox';
import Checkbox from '../../../components/CustomCheckbox/CustomCheckbox';
import Select from '../../../elements/Select';
import RequiredStar from '../../../elements/RequiredStar.js';

// Assets
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';
import Edit from '../../../assets/img/editing.svg';
import Delete from '../../../assets/img/deleting.svg';

// Utils
import { getMinutesFromTimeInput, minutesToTimeInput } from '../../../utils/timeCalc';
import { getShortDateFormat, shortDateFormat, timeFormat, dateToTimeFormat } from '../../../utils/dateFormat';
import { AUTO_TIMESHEET_DAY_KEYS } from '../../../utils/constants';
import AutoTimeRecordingDays from '~/components/RosterShiftOptions/AutoTimeRecordingDays';
import { checkUserPermissions } from '../../../library/stores/userStore';

// Styles
import './Schedule.css';

class RecurringShiftForm extends Component {
    constructor(props) {
        super(props);
        const { commonStore, userStore } = props;
        const currentUser = userStore.currentUser;

        // working shifts only - rest (F) codes and standby codes can't be a recurring shift
        this.workingCompanyShifts = (commonStore.config.companyShifts || []).filter(cs => cs.shift_type !== 'freeShift' && cs.shift_type !== 'standbyShift');
        this.companyShiftsOptions = this.workingCompanyShifts.map(cs => ({ label: cs.shift_name, value: cs.id }));
        // the working shift always comes from the user's profile (first working shift when there is none), same as the
        // member shift forms - it's view only, and the server also uses the current profile shift when generating
        this.profileShift = this.workingCompanyShifts.find(cs => cs.id === currentUser.company_shift_id) || this.workingCompanyShifts[0];

        const allowUserAutoTimesheetToggle = !!commonStore?.config?.client?.data?.loginRules?.allowUserToEnableDisableAutoTimesheet;
        this.isOwnAutoTimesheetProfileDisabled = allowUserAutoTimesheetToggle && currentUser.allow_user_for_auto_timesheet === false;
        // members need "Edit Auto Time Recording" to change it, same as the shift form - PMs are never restricted
        this.canEditAutoTimeRecording = currentUser.user_type !== 'member' || checkUserPermissions('md_Shift', 'editAutoTimeRecording');

        this.state = {
            view: 'list', // 'list' | 'form' | 'history'
            // the list opens on the current plan; upcoming and completed plans are one tab away.
            // kept as one object in state - GenericList reloads whenever the filters object changes
            appliedFilters: { status: 'current' },
            selectedPlan: null, // null while creating a new plan
            listKey: 0, // bumped after a delete so the plan list loads again

            ...this.getNewPlanFields(),

            shiftsFailedToCreate: null,
            hasShiftCreated: false,
            userPromptModal: {
                source: '',
                header: '',
                showModal: false,
                sourceInfo: {},
                actionMethods: {}
            },
        };
    }

    // empty form for a new plan - the user picks the From and To weeks
    getNewPlanFields = () => {
        return {
            name: '',
            start_date: null,
            end_date: null,
            company_shift_id: this.profileShift ? this.profileShift.id : null,
            start_time: this.profileShift ? this.profileShift.start_time : 0,
            end_time: this.profileShift ? this.profileShift.end_time : 0,
            defaultDays: [
                { day: 'Mon', key: 'monday', checked: false }, { day: 'Tue', key: 'tuesday', checked: false },
                { day: 'Wed', key: 'wednesday', checked: false }, { day: 'Thu', key: 'thursday', checked: false },
                { day: 'Fri', key: 'friday', checked: false }, { day: 'Sat', key: 'saturday', checked: false },
                { day: 'Sun', key: 'sunday', checked: false }, { day: 'Check All', checked: false },
            ],
            autoTimesheetDays: null,
            project_id: null,
            task_id: null,
            override_shift: false,
        };
    };

    // a plan's status comes from the server (its dates against the server's date) - never the browser clock
    getPlanState = (plan) => {
        if (plan.status === 'completed') return 'Completed';
        if (plan.status === 'upcoming') return 'Upcoming';
        return 'Current';
    };

    // GenericList data source, 5 per page, filtered by status on the server
    requestPlans = (params) => {
        return this.props.scheduleStore.getRecurringShifts(params);
    };

    requestHistory = (params) => {
        return this.props.scheduleStore.getRecurringShiftHistory(this.state.selectedPlan.id, params);
    };

    openNewPlan = () => {
        this.setState({ ...this.getNewPlanFields(), selectedPlan: null, view: 'form' });
    };

    openPlan = (plan) => {
        // the modal edits one shared configuration, so any selected day carries the shared values
        const schedule = plan.schedule || {};
        const firstDay = Object.values(schedule)[0] || {};
        const defaultDays = this.getNewPlanFields().defaultDays.map(item => ({ ...item, checked: item.key ? !!schedule[item.key] : false }));
        defaultDays[7].checked = defaultDays.slice(0, 7).every(item => item.checked);
        const autoTimesheetDays = {};
        AUTO_TIMESHEET_DAY_KEYS.forEach((key, index) => {
            const day = schedule[defaultDays[index].key];
            autoTimesheetDays[key] = day ? day.autoTimeRecording === true : false;
        });

        this.setState({
            view: 'form',
            selectedPlan: plan,
            name: plan.name,
            start_date: moment(plan.start_date).toDate().toString(),
            end_date: moment(plan.end_date).toDate().toString(),
            // a completed plan is history - it shows the shift it was saved with; any other plan shows the current profile shift
            company_shift_id: this.getPlanState(plan) === 'Completed' || !this.profileShift ? firstDay.companyShiftId : this.profileShift.id,
            start_time: firstDay.startTime,
            end_time: firstDay.endTime,
            defaultDays,
            autoTimesheetDays,
            project_id: firstDay.projectId,
            task_id: firstDay.taskId,
            override_shift: firstDay.overrideShift === true,
        });
    };

    // the list reloads itself when GenericList mounts again
    backToList = () => {
        this.setState({ view: 'list', selectedPlan: null, shiftsFailedToCreate: null });
    };

    handleCloseUserPromptModal = () => {
        this.setState({ userPromptModal: { ...this.state.userPromptModal, showModal: false } });
    };

    // the plan always covers whole weeks: the From picker offers only Mondays and the To picker only Sundays
    handleDateChange = (name, value) => {
        let { start_date, end_date } = this.state;
        if (name === 'start_date') start_date = value.toString();
        else end_date = value.toString();

        if (moment(end_date).isBefore(start_date)) {
            if (name === 'start_date') end_date = moment(start_date).endOf('isoWeek').toDate().toString();
            else start_date = moment(end_date).startOf('isoWeek').toDate().toString();
        }
        this.setState({ start_date, end_date });
    };

    handleCheck = (index, evt) => {
        const { defaultDays } = this.state;
        if (defaultDays[index].day === 'Check All') {
            this.setState({ defaultDays: defaultDays.map(item => ({ ...item, checked: evt })) });
            return;
        }
        const newDays = defaultDays.map((item, i) => i === index ? { ...item, checked: evt } : item);
        newDays[7] = { ...newDays[7], checked: newDays.slice(0, 7).every(item => item.checked) };
        this.setState({ defaultDays: newDays });
    };

    handleSave = async () => {
        const { name, start_date, end_date, company_shift_id, start_time, end_time, defaultDays, autoTimesheetDays,
            project_id, task_id, override_shift, selectedPlan } = this.state;
        const { scheduleStore, commonStore, t } = this.props;

        if (!name.trim()) return commonStore.addNotification(t('Please enter a name for the recurring shift plan'), null, 'warning');
        if (!start_date || !end_date) return commonStore.addNotification(t('Please select a valid week range'), null, 'warning');
        if (!company_shift_id) return commonStore.addNotification(t('Please select a working shift'), null, 'warning');
        if (!project_id || !task_id) return commonStore.addNotification(t('Please select project and task'), null, 'warning');
        if (!defaultDays.slice(0, 7).some(item => item.checked)) return commonStore.addNotification(t('Please select days to create shift'), null, 'warning');

        // only the selected weekdays are sent - every other weekday is ignored by the plan
        const schedule = {};
        defaultDays.slice(0, 7).forEach((item, index) => {
            if (!item.checked) return;
            schedule[item.key] = {
                startTime: start_time,
                endTime: end_time,
                companyShiftId: company_shift_id,
                projectId: parseInt(project_id),
                taskId: parseInt(task_id),
                autoTimeRecording: !this.isOwnAutoTimesheetProfileDisabled && !!autoTimesheetDays && autoTimesheetDays[AUTO_TIMESHEET_DAY_KEYS[index]] === true,
                overrideShift: override_shift,
            };
        });
        const values = {
            id: selectedPlan ? selectedPlan.id : undefined,
            name: name.trim(),
            start_date: moment(start_date).format('YYYY-MM-DD'),
            end_date: moment(end_date).format('YYYY-MM-DD'),
            schedule,
        };

        try {
            const res = await scheduleStore.saveRecurringShift(values, !selectedPlan);
            // nothing was saved - the overlapping plans are listed in a user prompt and the form stays open
            if (res?.userPrompts?.length) {
                const overlappingRecurringShiftsPlan = res.userPrompts.find(ele => ele.source === 'overlappingRecurringShiftsPlan');
                if (overlappingRecurringShiftsPlan?.showModal) {
                    this.setState({
                        userPromptModal: {
                            ...overlappingRecurringShiftsPlan,
                            actionMethods: {
                                handleCloseUserPromptModal: this.handleCloseUserPromptModal
                            }
                        }
                    });
                }
                return;
            }
            // back in the list, filter on the saved plan's status so it's visible - a new plan is often upcoming, not current
            const savedPlanStatus = this.getPlanState(res.recurringShift).toLowerCase();
            // the message comes from the server, already translated - a plain save and a split are both successes
            if (res?.shiftsFailedToCreate?.length) {
                commonStore.addNotification(res.message, null, 'warning');
                this.setState({ shiftsFailedToCreate: res.shiftsFailedToCreate, hasShiftCreated: true, appliedFilters: { status: savedPlanStatus } });
            } else {
                commonStore.addNotification(res?.message || t('Recurring shift plan saved successfully'), null, 'success');
                this.setState({ hasShiftCreated: true, appliedFilters: { status: savedPlanStatus } }, this.backToList);
            }
        } catch (error) {
            commonStore.addNotification(t(error.message || 'Error'), null, 'error');
            console.error(error);
        }
    };

    handleDelete = (plan) => {
        const { scheduleStore, commonStore, t } = this.props;
        confirmAlert({
            title: t('Delete'),
            message: `${t('Are you sure you want to delete this recurring shift plan?')} ${plan.name}`,
            buttons: [
                {
                    label: t('Delete'),
                    onClick: async () => {
                        try {
                            const res = await scheduleStore.removeRecurringShift(plan.id);
                            commonStore.addNotification(res?.message || t('Recurring shift plan deleted successfully'), null, 'success');
                            this.setState({ hasShiftCreated: true, listKey: this.state.listKey + 1 }, this.backToList);
                        } catch (error) {
                            commonStore.addNotification(t(error.message || 'Error'), null, 'error');
                        }
                    },
                },
                {
                    label: t('Cancel'),
                    onClick: () => { },
                },
            ],
        });
    };

    // a past plan is read only, and only a plan that hasn't started yet can be deleted (same rules as the server)
    renderPlanActionButtons = (plan) => {
        const { t } = this.props;
        const planState = this.getPlanState(plan);
        return (
            <div className="actions-center">
                {/* a completed plan is read only - it opens for viewing instead of editing */}
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-edit-recurring-shift">{t(planState === 'Completed' ? 'View' : 'Edit')}</Tooltip>}
                >
                    <Button icon_sm fill onClick={() => this.openPlan(plan)}>
                        {planState === 'Completed' ? <i className="fa fa-eye" /> : <img src={Edit} alt="edit button" />}
                    </Button>
                </OverlayTrigger>
                {planState === 'Upcoming' && (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="tooltip-delete-recurring-shift">{t('Delete')}</Tooltip>}
                    >
                        <Button icon_sm_delete fill onClick={() => this.handleDelete(plan)}>
                            <img src={Delete} alt="delete button" />
                        </Button>
                    </OverlayTrigger>
                )}
            </div>
        );
    };

    // same look as the cycle settings icon in the work pattern assignments list
    renderPlanSettingsInfoIcon = (plan) => {
        const { t, commonStore } = this.props;
        // same gate as the auto time recording picker in the form
        const allowShiftAutoTimesheetToggle = !!commonStore?.config?.client?.data?.loginRules?.allowShiftToEnableDisableAutoTimesheet;
        const schedule = plan.schedule || {};
        const firstDay = Object.values(schedule)[0] || {};
        const autoRecordingDayLabels = this.state.defaultDays.slice(0, 7)
            .filter(item => schedule[item.key] && schedule[item.key].autoTimeRecording === true)
            .map(item => t(item.day));

        return (
            <OverlayTrigger placement="top" overlay={
                <Tooltip id={`recurring-shift-settings-tooltip-${plan.id}`}>
                    <div className="text-start">
                        <div><strong>{t('Override shift')}</strong>: {firstDay.overrideShift ? t('On') : t('Off')}</div>
                        {allowShiftAutoTimesheetToggle && (
                            <div><strong>{t('Auto time recording')}</strong>: {autoRecordingDayLabels.length ? autoRecordingDayLabels.join(', ') : t('None')}</div>
                        )}
                    </div>
                </Tooltip>
            }>
                <i className="fa fa-info-circle text-muted flex-shrink-0 cursor-pointer" style={{ opacity: '0.6' }} aria-hidden="true"></i>
            </OverlayTrigger>
        );
    };

    // status filter as tab buttons - the selected one is filled (same toggle style as the member shift form)
    renderStatusTabs() {
        const { t } = this.props;
        const { status } = this.state.appliedFilters;
        return (
            <div className="d-flex gap-2">
                {[
                    { value: 'current', label: 'Current' },
                    { value: 'upcoming', label: 'Upcoming' },
                    { value: 'completed', label: 'Completed' },
                ].map(tab => (
                    <Button
                        key={tab.label}
                        fill={status === tab.value}
                        variant={status === tab.value ? undefined : 'outline-secondary'}
                        onClick={() => { if (status !== tab.value) this.setState({ appliedFilters: { status: tab.value } }); }}
                    >
                        {t(tab.label)}
                    </Button>
                ))}
            </div>
        );
    }

    renderPlanList() {
        const { t, commonStore } = this.props;
        const dateTimeRules = commonStore.getDateTimeRules();
        return (
            <GenericList
                columns={[
                    {
                        Header: t('Name'),
                        id: 'name',
                        accessor: ({ name }) => <div className="text-truncate" title={name}>{name}</div>,
                        textAlign: 'start',
                        maxWidth: 90,
                    },
                    {
                        Header: t('From date'),
                        id: 'start_date',
                        accessor: ({ start_date }) => shortDateFormat(start_date, dateTimeRules),
                        textAlign: 'start',
                        maxWidth: 90,
                    },
                    {
                        Header: t('To date'),
                        id: 'end_date',
                        accessor: ({ end_date }) => shortDateFormat(end_date, dateTimeRules),
                        textAlign: 'start',
                        maxWidth: 90,
                    },
                    {
                        Header: t('Weekdays'),
                        id: 'schedule',
                        accessor: (plan) => {
                            const schedule = plan.schedule || {};
                            const firstDay = Object.values(schedule)[0] || {};
                            return (
                                <div>
                                    {this.state.defaultDays.slice(0, 7).filter(item => schedule[item.key]).map(item => t(item.day)).join(', ')}
                                    <div className="d-flex align-items-center gap-1">
                                        <span>{timeFormat(firstDay.startTime, dateTimeRules)} - {timeFormat(firstDay.endTime, dateTimeRules)}</span>
                                        {this.renderPlanSettingsInfoIcon(plan)}
                                    </div>
                                </div>
                            );
                        },
                        textAlign: 'start',
                        width: 180,
                    },
                    {
                        Header: t('Version'),
                        accessor: 'version_no',
                        textAlign: 'center',
                        maxWidth: 70,
                    },
                    {
                        Header: t('Status'),
                        id: 'status',
                        accessor: (plan) => {
                            const planState = this.getPlanState(plan);
                            // full cell width so every status is the same size, rounded at the sides
                            if (planState === 'Current') return <Badge className="bg-success rounded-pill d-block w-100" style={{ padding: '6px 8px', fontSize: '12px' }}>{t('Current')}</Badge>;
                            if (planState === 'Upcoming') return <Badge className="bg-info rounded-pill d-block w-100" style={{ padding: '6px 8px', fontSize: '12px' }}>{t('Upcoming')}</Badge>;
                            return <Badge className="bg-secondary rounded-pill d-block w-100" style={{ padding: '6px 8px', fontSize: '12px' }}>{t('Completed')}</Badge>;
                        },
                        textAlign: 'center',
                        maxWidth: 100,
                    },
                    {
                        Header: t('Actions'),
                        id: 'actions',
                        accessor: (plan) => this.renderPlanActionButtons(plan),
                        textAlign: 'center',
                        maxWidth: 100,
                    },
                ]}
                key={this.state.listKey}
                // headers follow their column's textAlign
                alignColumns
                filters={this.state.appliedFilters}
                requestData={this.requestPlans}
                pageSize={5}
                // full-width header row: status tabs on the left, Add new on the right
                splitHeader
            >
                <Row className="align-items-center mt-2 gx-0">
                    <Col>
                        {this.renderStatusTabs()}
                    </Col>
                    <Col xs="auto" className="text-end">
                        <Button fill wd onClick={this.openNewPlan}>
                            <i className="fa fa-plus-circle fa-xl margin-right-10" />
                            {t('Add new')}
                        </Button>
                    </Col>
                </Row>
            </GenericList>
        );
    }

    renderHistory() {
        const { t, commonStore } = this.props;
        const { selectedPlan } = this.state;
        const dateTimeRules = commonStore.getDateTimeRules();
        return (
            <div className="row">
                {/* the plan as it is saved now - read only here, it's edited in the form */}
                <div className="row m-0">
                    <div className="col-md-4">
                        <div className="form-group field field-string">
                            <label className="control-label">{t('Name')}</label>
                            <input className="form-control" value={selectedPlan.name} disabled />
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="form-group field field-string">
                            <label className="control-label">{t('From date')}</label>
                            <br />
                            <DatePicker
                                selected={moment(selectedPlan.start_date).toDate()}
                                onChange={() => { }}
                                dateFormat={getShortDateFormat(dateTimeRules)}
                                disabled
                            />
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="form-group field field-string">
                            <label className="control-label">{t('To date')}</label>
                            <br />
                            <DatePicker
                                selected={moment(selectedPlan.end_date).toDate()}
                                onChange={() => { }}
                                dateFormat={getShortDateFormat(dateTimeRules)}
                                disabled
                            />
                        </div>
                    </div>
                </div>

                <div className="mt-3">
                    <GenericList
                        tableHeader={t('History')}
                        // headers follow their column's textAlign
                        alignColumns
                        columns={[
                            {
                                Header: t('Version'),
                                accessor: 'version_no',
                                textAlign: 'center',
                                maxWidth: 80,
                            },
                            {
                                Header: t('Name'),
                                id: 'name',
                                accessor: ({ name }) => <span>{name}</span>,
                                textAlign: 'start',
                            },
                            {
                                Header: t('From date'),
                                id: 'start_date',
                                accessor: ({ start_date }) => shortDateFormat(start_date, dateTimeRules),
                                textAlign: 'start',
                                maxWidth: 90,
                            },
                            {
                                Header: t('To date'),
                                id: 'end_date',
                                accessor: ({ end_date }) => shortDateFormat(end_date, dateTimeRules),
                                textAlign: 'start',
                                maxWidth: 90,
                            },
                            {
                                Header: t('Weekdays'),
                                id: 'schedule',
                                accessor: (version) => {
                                    // every selected day shares the same time, so show it once
                                    const schedule = version.schedule || {};
                                    const firstDay = Object.values(schedule)[0] || {};
                                    return (
                                        <div>
                                            {this.state.defaultDays.slice(0, 7).filter(item => schedule[item.key]).map(item => t(item.day)).join(', ')}
                                            <div className="d-flex align-items-center gap-1">
                                                <span>{timeFormat(firstDay.startTime, dateTimeRules)} - {timeFormat(firstDay.endTime, dateTimeRules)}</span>
                                                {/* a history version carries the same schedule shape as the plan */}
                                                {this.renderPlanSettingsInfoIcon(version)}
                                            </div>
                                        </div>
                                    );
                                },
                                textAlign: 'start',
                                width: 180,
                            },
                            {
                                Header: t('Saved on'),
                                id: 'createdAt',
                                accessor: ({ createdAt }) => <span>{shortDateFormat(createdAt, dateTimeRules)} {dateToTimeFormat(createdAt, dateTimeRules)}</span>,
                                textAlign: 'start',
                                maxWidth: 150,
                            },
                        ]}
                        requestData={this.requestHistory}
                        pageSize={5}
                    />
                </div>
            </div>
        );
    }

    renderForm() {
        const { t, commonStore } = this.props;
        const { name, start_date, end_date, company_shift_id, start_time, end_time, defaultDays, autoTimesheetDays,
            project_id, task_id, override_shift, selectedPlan } = this.state;
        const dateTimeRules = commonStore.getDateTimeRules();
        const format = dateTimeRules?.time_format || 'hh:mm';
        const planState = selectedPlan ? this.getPlanState(selectedPlan) : 'Upcoming';
        // a past plan is history - read only; a current plan keeps its start week
        const isReadOnly = planState === 'Completed';
        // same rule as the server: a To date after the first Sunday of the next year means one plan per year
        let willSplitByYear = false;
        if (start_date && end_date) {
            const firstSundayOfNextYear = moment({ year: moment(start_date).year() + 1, month: 0, date: 1 }).isoWeekday(7);
            willSplitByYear = moment(end_date).isAfter(firstSundayOfNextYear, 'day');
        }

        return (
            <div className="row">
                <div className="row m-0">
                    <div className="col-md-4">
                        <div className="form-group field field-string">
                            <label className="control-label">{t('Name')} <RequiredStar /></label>
                            <input className="form-control" value={name} maxLength={150} disabled={isReadOnly} onChange={e => this.setState({ name: e.target.value })} />
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="form-group field field-string">
                            <label className="control-label">{t('From date')} <RequiredStar /></label>
                            <br />
                            <DatePicker
                                selected={start_date ? new Date(start_date) : null}
                                onChange={(date) => this.handleDateChange('start_date', date)}
                                dateFormat={getShortDateFormat(dateTimeRules)}
                                filterDate={date => moment(date).isoWeekday() === 1}
                                minDate={moment().startOf('isoWeek').toDate()}
                                showWeekNumbers
                                disabled={planState !== 'Upcoming'}
                            />
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="form-group field field-string">
                            <label className="control-label">{t('To date')} <RequiredStar /></label>
                            <br />
                            <DatePicker
                                selected={end_date ? new Date(end_date) : null}
                                onChange={(date) => this.handleDateChange('end_date', date)}
                                dateFormat={getShortDateFormat(dateTimeRules)}
                                filterDate={date => moment(date).isoWeekday() === 7}
                                minDate={moment().endOf('isoWeek').startOf('day').toDate()}
                                showWeekNumbers
                                disabled={isReadOnly}
                            />
                        </div>
                    </div>
                    {willSplitByYear && !isReadOnly && (
                        <div className="col-12 text-muted mt-1" style={{ fontSize: '13px' }}>
                            <i className="fa fa-info-circle me-1" aria-hidden="true" />
                            {t('The selected weeks cross into the new year, so the plan will be split into one plan per year')}
                        </div>
                    )}
                </div>

                <div className="row m-0 mt-3">
                    <div className="col-md-4">
                        <div className="form-group field field-string">
                            <label className="control-label fs15">{t('Working Shift')}</label>
                            {/* view only for members and PMs - it comes from the profile */}
                            <Select
                                options={this.companyShiftsOptions}
                                value={this.companyShiftsOptions.find(s => s.value === company_shift_id)}
                                isDisabled
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="form-group field field-string">
                            <label className="control-label fs15">{t('From')}</label>
                            <TimePickerInput
                                value={minutesToTimeInput(start_time, false)}
                                format={format === 'hh:mm' ? 'HH:mm' : format}
                                onChange={value => this.setState({ start_time: getMinutesFromTimeInput(value) })}
                                className="worklog-input"
                                disabled={isReadOnly}
                            />
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="form-group field field-string">
                            <label className="control-label fs15">{t('To')}</label>
                            <TimePickerInput
                                value={minutesToTimeInput(end_time, false)}
                                format={format === 'hh:mm' ? 'HH:mm' : format}
                                onChange={value => this.setState({ end_time: getMinutesFromTimeInput(value) })}
                                className="worklog-input"
                                disabled={isReadOnly}
                            />
                        </div>
                    </div>
                </div>

                <div className="row m-0 mt-3">
                    <div className="d-flex flex-wrap">
                        <label className="control-label fs15 mt-1">{t('Weekdays')}<RequiredStar /></label>
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip-recurring-weekdays">{t('Please select the weekdays for which you want to create shifts')}.</Tooltip>}
                        >
                            <i className="fa fa-info-circle text-muted mt-1 mx-2" style={{ opacity: '0.5' }} aria-hidden="true" />
                        </OverlayTrigger>
                        {defaultDays.map((item, index) => (
                            <CheckboxButton
                                key={item.day}
                                isChecked={item.checked}
                                onChange={evt => this.handleCheck(index, evt)}
                                label={t(item.day)}
                                isRightPadding={10}
                                isSatSun={index === 5 || index === 6}
                                className={index === 7 ? '' : 'overflow-property'}
                                disabled={isReadOnly}
                            />
                        ))}
                    </div>
                </div>

                <div className="row m-0 mt-2">
                    <div className="d-flex flex-wrap">
                        <AutoTimeRecordingDays
                            autoTimesheetDays={autoTimesheetDays}
                            onChange={(days) => this.setState({ autoTimesheetDays: days })}
                            disabledDayKeys={
                                this.isOwnAutoTimesheetProfileDisabled
                                    ? AUTO_TIMESHEET_DAY_KEYS
                                    : AUTO_TIMESHEET_DAY_KEYS.filter((k, i) => !defaultDays[i]?.checked)
                            }
                            // view only on a completed plan, or for a member without "Edit Auto Time Recording" - the values still show
                            disabled={isReadOnly || !this.canEditAutoTimeRecording}
                            labelClassName="auto-timesheet-days-label"
                        />
                    </div>
                </div>

                <div className="row m-0 mt-3">
                    <div className="col-md-6">
                        <div className="form-group field field-string">
                            <label className="control-label">{t('Project')} <RequiredStar /></label>
                            <br />
                            <ProjectsWidget
                                value={project_id}
                                onChange={(e) => this.setState({ project_id: e, task_id: null })}
                                applyOnFirstLoad={false}
                                fromShift={false}
                                maxMenuHeight={150}
                                disabled={isReadOnly}
                            />
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <Checkbox
                                isChecked={override_shift}
                                onChange={checked => this.setState({ override_shift: checked })}
                                label={t('Override shift')}
                                wrapperClassName="fs15"
                                disabled={isReadOnly}
                            />
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-recurring-override">{t('When enabled, existing shifts on the selected days will be replaced by the recurring shift')}.</Tooltip>}
                            >
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true" />
                            </OverlayTrigger>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group field field-string">
                            <label className="control-label">{t('Task')} <RequiredStar /></label>
                            <br />
                            <TasksWidget
                                className="form-control"
                                onChange={(value) => this.setState({ task_id: value })}
                                value={task_id}
                                defaultIfOne
                                defaultByGeneric
                                filterByProject={project_id}
                                maxMenuHeight={150}
                                disabled={isReadOnly}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderBody() {
        const { t, commonStore } = this.props;
        const { view, shiftsFailedToCreate } = this.state;
        if (shiftsFailedToCreate) {
            return (
                <div className="mt-3">
                    <AfterBulkCreateFailedResponseModalBody
                        shiftsFailedToCreate={shiftsFailedToCreate}
                        dateTimeRules={commonStore.getDateTimeRules()}
                        t={t}
                    />
                </div>
            );
        }
        if (view === 'form') return this.renderForm();
        if (view === 'history') return this.renderHistory();
        return this.renderPlanList();
    }

    renderFooterButtons() {
        const { t } = this.props;
        const { view, selectedPlan, shiftsFailedToCreate, hasShiftCreated } = this.state;
        if (shiftsFailedToCreate) {
            return <Button wd onClick={this.backToList}>{t('Back')}</Button>;
        }
        if (view === 'list') {
            return <Button wd onClick={() => this.props.finish(hasShiftCreated)}>{t('Close')}</Button>;
        }
        if (view === 'history') {
            return <Button wd onClick={() => this.setState({ view: 'form' })}>{t('Back')}</Button>;
        }
        const planState = selectedPlan ? this.getPlanState(selectedPlan) : 'Upcoming';
        return (
            <>
                {planState !== 'Completed' && (
                    <div>
                        <Button fill wd type="submit" onClick={this.handleSave}>
                            {t(selectedPlan ? 'Update' : 'Create')}
                        </Button>
                    </div>
                )}
                {/* only a plan that hasn't started yet can be deleted */}
                {selectedPlan && planState === 'Upcoming' && (
                    <div>
                        <Button wd onClick={() => this.handleDelete(selectedPlan)}>{t('Delete')}</Button>
                    </div>
                )}
                <div>
                    <Button wd onClick={this.backToList}>{t('Back')}</Button>
                </div>
                {/* history sits apart on the right side - it opens a separate view, it's not a form action */}
                {selectedPlan && (
                    <div className="ms-auto">
                        <Button wd onClick={() => this.setState({ view: 'history' })}>
                            <i className="fa fa-history margin-right-10" />
                            {t('History')}
                        </Button>
                    </div>
                )}
            </>
        );
    }

    render() {
        const { t } = this.props;
        const { hasShiftCreated, userPromptModal } = this.state;

        return (
            <>
                <Modal dialogClassName="lg bulk-shift-modal" className="lg" size="lg" show onHide={() => this.props.finish(hasShiftCreated)}>
                    <Modal.Header className="set_ModalHeader">
                        <Modal.Title className="w-100 text-center custom-modal-title">
                            {t('Recurring Shifts')}
                        </Modal.Title>
                        <img
                            src={CloseModalIcon}
                            alt="closeIcon"
                            className="cursor-pointer"
                            onClick={() => this.props.finish(hasShiftCreated)}
                        />
                    </Modal.Header>

                    <Modal.Body style={{ minHeight: '400px' }}>
                        {this.renderBody()}
                    </Modal.Body>

                    <Modal.Footer className="border-0">
                        <div className="row m-0 w-100 mb-4">
                            <div className="col-12 d-flex gap-4">
                                {this.renderFooterButtons()}
                            </div>
                        </div>
                    </Modal.Footer>
                </Modal>

                <UserPromptModal
                    source={userPromptModal.source}
                    header={userPromptModal.header}
                    showModal={userPromptModal.showModal}
                    sourceInfo={userPromptModal.sourceInfo}
                    actionMethods={userPromptModal.actionMethods}
                />
            </>
        );
    }
}

export default inject('userStore', 'commonStore', 'scheduleStore')(
    applicationRouter(withLocalization(observer(RecurringShiftForm)))
);
